// [claude-code 2026-03-11] T2: FloatingWidget now accepts IVScoreResponse from backend
import { useEffect, useState } from "react";
import { IVScoreCard } from "../IVScoreCard";
import { EmotionalResonanceMonitor } from "../mission-control/EmotionalResonanceMonitor";
import type { IVScoreResponse } from "../../types/market-data";
import { X } from "lucide-react";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

type LayoutOption = "tickers-only" | "combined";

interface FloatingWidgetProps {
  ivData: IVScoreResponse | null;
  ivLoading?: boolean;
  layoutOption?: LayoutOption;
  onClose?: () => void;
}

export function FloatingWidget({
  ivData,
  ivLoading,
  layoutOption = "combined",
  onClose,
}: FloatingWidgetProps) {
  const [, setErScore] = useState<number>(0);
  const [showERCard, setShowERCard] = useState(false);

  // Listen for ER score updates
  useEffect(() => {
    const handleERUpdate = (event: CustomEvent<number>) => {
      setErScore(event.detail);
      setShowERCard(true);
    };
    window.addEventListener("erScoreUpdate", handleERUpdate as EventListener);
    return () => {
      window.removeEventListener(
        "erScoreUpdate",
        handleERUpdate as EventListener,
      );
    };
  }, []);

  return (
    <>
      <div className="fixed top-[70px] left-4 z-50 flex flex-col items-start gap-2">
        {/* IV Score Tickers - Frosted Glass Effect (iOS 26 style) */}
        {/* Only show VIX ticker when NOT in tickers-only layout */}
        {layoutOption !== "tickers-only" && (
          <div
            className="flex items-center gap-2 backdrop-blur-3xl bg-gradient-to-br from-[var(--fintheon-surface)]/50 via-[var(--fintheon-surface)]/40 to-[var(--fintheon-surface)]/30 border border-[var(--fintheon-accent)]/30 rounded-2xl p-2.5 shadow-2xl shadow-black/50"
            style={{
              backdropFilter: "blur(40px) saturate(180%)",
              WebkitBackdropFilter: "blur(40px) saturate(180%)",
              boxShadow:
                "0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 1px 0 rgba(255, 255, 255, 0.1)",
            }}
          >
            <div
              className="backdrop-blur-2xl bg-gradient-to-br from-[var(--fintheon-bg)]/60 to-[var(--fintheon-bg)]/40 border border-zinc-800/60 rounded-xl px-2.5 py-1"
              style={{
                backdropFilter: "blur(20px) saturate(150%)",
                WebkitBackdropFilter: "blur(20px) saturate(150%)",
              }}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] text-gray-300 drop-shadow-sm">
                  VIX
                </span>
                <span className="text-xs font-mono text-gray-100 drop-shadow-sm">
                  <FintheonNumericText
                    text={String(ivData ? ivData.vix.level.toFixed(2) : "--")}
                    kind="instrument-price"
                  />
                </span>
              </div>
            </div>
            <IVScoreCard
              data={ivData}
              loading={ivLoading}
              layoutOption={layoutOption}
            />
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-[var(--fintheon-accent)]/20 rounded-xl text-[var(--fintheon-accent)]/80 hover:text-[var(--fintheon-accent)] backdrop-blur-sm transition-all"
                title="Close Widget"
                style={{
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                }}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* ER Monitor Card - Landscape oriented, drops down with frosted glass */}
        {layoutOption !== "tickers-only" && showERCard && (
          <div
            className="backdrop-blur-3xl bg-gradient-to-br from-[var(--fintheon-surface)]/50 via-[var(--fintheon-surface)]/40 to-[var(--fintheon-surface)]/30 border border-[var(--fintheon-accent)]/30 rounded-2xl p-4 w-96 transition-all duration-300 opacity-100 translate-y-0 animate-slide-down shadow-2xl"
            style={{
              backdropFilter: "blur(40px) saturate(180%)",
              WebkitBackdropFilter: "blur(40px) saturate(180%)",
              boxShadow:
                "0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 1px 0 rgba(255, 255, 255, 0.1)",
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-[var(--fintheon-accent)] drop-shadow-sm">
                Emotional Resonance
              </h3>
              <button
                onClick={() => setShowERCard(false)}
                className="p-1.5 hover:bg-[var(--fintheon-accent)]/20 rounded-xl text-[var(--fintheon-accent)]/70 hover:text-[var(--fintheon-accent)] backdrop-blur-sm transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto">
              <EmotionalResonanceMonitor onERScoreChange={setErScore} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
